/**
 * AvatarViewer - Self-hosted GLB avatar renderer
 * Replaces @readyplayerme/visage with direct React Three Fiber implementation
 *
 * Features persistent caching via Cache API for faster subsequent page loads.
 */

import React, { Suspense, useRef, useEffect, useMemo, useState, Component, ErrorInfo, ReactNode } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, ContactShadows, useFBX, Html } from '@react-three/drei';
import * as THREE from 'three';
import { preloadAvatarToCache } from '../../helpers/avatarCache';

// Error boundary for catching 3D rendering errors
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AvatarViewer error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Loading indicator component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded-lg">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

// Error display component
function ErrorFallback({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full w-full bg-gray-100 rounded-lg p-4">
      <div className="text-red-500 text-4xl mb-2">⚠️</div>
      <div className="text-gray-600 text-sm text-center">{message}</div>
    </div>
  );
}

interface AvatarModelProps {
  modelSrc: string;
  animationSrc?: string;
  scale?: number;
  position?: [number, number, number];
}

// 3D Loading indicator inside canvas
function Loader3D() {
  return (
    <Html center>
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <div className="text-sm text-gray-500 mt-2">Loading avatar...</div>
      </div>
    </Html>
  );
}

// Constants from @readyplayerme/visage for Mixamo animation normalization
const MIXAMO_PREFIX = 'mixamorig';
const POSITION_SUFFIX = '.position';
const MIXAMO_SCALE = 0.01;

// Normalize FBX animations for RPM avatars (matches visage implementation)
function normalizeFbxAnimation(clip: THREE.AnimationClip): THREE.AnimationClip {
  const newTracks: THREE.KeyframeTrack[] = [];

  for (const track of clip.tracks) {
    const newTrack = track.clone();

    // Remove mixamorig prefix from bone names
    if (newTrack.name.includes(MIXAMO_PREFIX)) {
      newTrack.name = newTrack.name.replace(MIXAMO_PREFIX, '');
    }

    // Scale position values (Mixamo exports at 100x scale)
    if (newTrack.name.includes(POSITION_SUFFIX)) {
      for (let i = 0; i < newTrack.values.length; i++) {
        newTrack.values[i] = newTrack.values[i] * MIXAMO_SCALE;
      }
    }

    newTracks.push(newTrack);
  }

  return new THREE.AnimationClip(clip.name, clip.duration, newTracks);
}

// Separate component for avatar with animation - always loads both
function AvatarWithAnimation({ modelSrc, animationSrc, scale = 1, position = [0, 0, 0] }: AvatarModelProps & { animationSrc: string }) {
  const group = useRef<THREE.Group>(null);
  const { scene, nodes } = useGLTF(modelSrc) as any;
  const fbx = useFBX(animationSrc);

  // Setup shadows on meshes
  useEffect(() => {
    scene.traverse((child: THREE.Object3D) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [scene]);

  // Normalize FBX animations (remove mixamorig prefix, scale positions)
  const normalizedAnimations = useMemo(() => {
    if (!fbx.animations.length) return [];

    const normalized = fbx.animations.map(clip => normalizeFbxAnimation(clip));

    return normalized;
  }, [fbx.animations]);

  // Find the Armature node for the animation mixer (like visage does)
  const armature = useMemo(() => {
    return nodes?.Armature || scene;
  }, [nodes, scene]);

  // Create mixer bound to the Armature
  const mixer = useMemo(() => new THREE.AnimationMixer(armature), [armature]);

  useEffect(() => {
    if (normalizedAnimations.length > 0) {
      const action = mixer.clipAction(normalizedAnimations[0]);
      action.play();

      return () => {
        action.stop();
        mixer.stopAllAction();
      };
    }
  }, [mixer, normalizedAnimations]);

  useFrame((_, delta) => {
    mixer.update(delta);
  });

  return (
    <group ref={group} position={position} scale={scale} dispose={null}>
      <primitive object={scene} />
    </group>
  );
}

// Simple avatar without animation
function AvatarStatic({ modelSrc, scale = 1, position = [0, 0, 0] }: Omit<AvatarModelProps, 'animationSrc'>) {
  const gltf = useGLTF(modelSrc);

  // Clone the scene to avoid shared reference issues
  const scene = useMemo(() => {
    const clone = gltf.scene.clone(true);

    // Re-bind skinned meshes
    const skinnedMeshes: THREE.SkinnedMesh[] = [];
    clone.traverse((node) => {
      if ((node as THREE.SkinnedMesh).isSkinnedMesh) {
        skinnedMeshes.push(node as THREE.SkinnedMesh);
      }
    });

    skinnedMeshes.forEach((mesh) => {
      if (mesh.skeleton) {
        const boneNames = mesh.skeleton.bones.map(b => b.name);
        const newBones: THREE.Bone[] = [];
        boneNames.forEach((name) => {
          clone.traverse((node) => {
            if (node.name === name && (node as THREE.Bone).isBone) {
              newBones.push(node as THREE.Bone);
            }
          });
        });
        if (newBones.length === boneNames.length) {
          const newSkeleton = new THREE.Skeleton(newBones);
          mesh.bind(newSkeleton, mesh.bindMatrix);
        }
      }
    });

    return clone;
  }, [gltf.scene]);

  useEffect(() => {
    scene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [scene]);

  return (
    <group position={position} scale={scale} dispose={null}>
      <primitive object={scene} />
    </group>
  );
}

interface AvatarViewerProps {
  modelSrc: string;
  animationSrc?: string;
  cameraInitialDistance?: number;
  cameraTarget?: number;
  fov?: number;
  scale?: number;
  halfBody?: boolean;
  shadows?: boolean;
  style?: React.CSSProperties;
  keyLightIntensity?: number;
  headMovement?: boolean;
}

export default function AvatarViewer({
  modelSrc,
  animationSrc,
  cameraInitialDistance = 2.5,
  cameraTarget = 1.5,
  fov = 50,
  scale = 1,
  halfBody = false,
  shadows = true,
  style,
  keyLightIntensity = 1.5,
}: AvatarViewerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const cameraPosition: [number, number, number] = [0, cameraTarget, cameraInitialDistance];
  const avatarPosition: [number, number, number] = [0, halfBody ? -0.5 : 0, 0];

  // Cache the model and animation for future page visits
  useEffect(() => {
    if (modelSrc) {
      preloadAvatarToCache(modelSrc);
    }
    if (animationSrc) {
      preloadAvatarToCache(animationSrc);
    }
  }, [modelSrc, animationSrc]);

  // Validate modelSrc
  if (!modelSrc) {
    return <ErrorFallback message="No avatar model specified" />;
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', ...style }}>
      <ErrorBoundary fallback={<ErrorFallback message="Failed to load 3D avatar" />}>
        {isLoading && (
          <div className="absolute inset-0 z-10">
            <LoadingFallback />
          </div>
        )}
        <Canvas
          camera={{
            position: cameraPosition,
            fov,
            near: 0.1,
            far: 100,
          }}
          shadows={shadows}
          onCreated={() => setIsLoading(false)}
        >
          <Suspense fallback={<Loader3D />}>
            {/* Lighting */}
            <ambientLight intensity={0.6} />
            <directionalLight
              position={[5, 5, 5]}
              intensity={keyLightIntensity}
              castShadow
              shadow-mapSize={[1024, 1024]}
            />
            <directionalLight position={[-5, 5, -5]} intensity={0.5} />

            {/* Avatar - conditionally render with or without animation */}
            {animationSrc ? (
              <AvatarWithAnimation
                modelSrc={modelSrc}
                animationSrc={animationSrc}
                scale={scale}
                position={avatarPosition}
              />
            ) : (
              <AvatarStatic
                modelSrc={modelSrc}
                scale={scale}
                position={avatarPosition}
              />
            )}

            {/* Ground shadow */}
            {shadows && (
              <ContactShadows
                position={[0, 0, 0]}
                opacity={0.4}
                scale={10}
                blur={2}
                far={4}
              />
            )}

            {/* Environment lighting */}
            <Environment preset="studio" />

            {/* Camera controls */}
            <OrbitControls
              target={[0, cameraTarget, 0]}
              enablePan={false}
              enableZoom={true}
              minDistance={1}
              maxDistance={5}
              minPolarAngle={Math.PI / 4}
              maxPolarAngle={Math.PI / 2}
            />
          </Suspense>
        </Canvas>
      </ErrorBoundary>
    </div>
  );
}

// Preload helpers - also cache to Cache API for persistence across page loads
export function preloadAvatar(modelSrc: string) {
  // Preload into Three.js memory cache
  useGLTF.preload(modelSrc);
  // Also cache to Cache API for persistence across sessions
  preloadAvatarToCache(modelSrc);
}

export function preloadAnimation(animationSrc: string) {
  // Preload into Three.js memory cache
  useFBX.preload(animationSrc);
  // Also cache to Cache API for persistence across sessions
  preloadAvatarToCache(animationSrc);
}
